import React from "react";
import { CONTACT, FOOTER } from "@/constants/testIds";
import { MapPin, Phone, Mail, Clock } from "lucide-react";

export const Contact = () => {
  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="section bg-[#1A1A1A] text-[#F9F8F6]"
    >
      <div className="container-wide">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-6">
            <span className="label-overline" style={{ color: "#B89F82" }}>
              Get in touch
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-7xl mt-5 leading-[1] font-light">
              Visit our showroom,
              <br />
              <span className="italic text-[#B89F82]">design with us.</span>
            </h2>
            <p className="mt-8 text-[#C9C4BC] leading-relaxed max-w-lg">
              Our trade team works with architects, builders, and homeowners across the
              South Florida region. Reach out — and we’ll set time aside for samples,
              slab walks, and project consultations.
            </p>
          </div>

          <div className="md:col-span-5 md:col-start-8 space-y-10">
            <div data-testid={CONTACT.address}>
              <div className="flex items-center gap-3 mb-3">
                <MapPin size={16} className="text-[#B89F82]" />
                <span className="label-overline" style={{ color: "#B89F82" }}>Showroom</span>
              </div>
              <p className="font-heading text-2xl font-light leading-snug">
                4260 NW 1st Avenue
                <br />
                Boca Raton, Florida 33431
              </p>
              <a
                href="https://www.google.com/maps/search/?api=1&query=4260+NW+1st+Avenue+Boca+Raton+Florida+33431"
                target="_blank"
                rel="noopener noreferrer"
                className="link-elegant text-sm text-[#B89F82] mt-3 inline-block"
              >
                Get directions
              </a>
            </div>

            <div data-testid={CONTACT.phone}>
              <div className="flex items-center gap-3 mb-3">
                <Phone size={16} className="text-[#B89F82]" />
                <span className="label-overline" style={{ color: "#B89F82" }}>Phone</span>
              </div>
              <a
                href="tel:+15618664203"
                className="font-heading text-2xl font-light hover:text-[#B89F82] transition-colors"
              >
                (561) 866-4203
              </a>
            </div>

            <div data-testid={CONTACT.email}>
              <div className="flex items-center gap-3 mb-3">
                <Mail size={16} className="text-[#B89F82]" />
                <span className="label-overline" style={{ color: "#B89F82" }}>Email</span>
              </div>
              <a
                href="mailto:info@instonesupplies.com"
                className="font-heading text-2xl font-light hover:text-[#B89F82] transition-colors break-all"
              >
                info@instonesupplies.com
              </a>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-3">
                <Clock size={16} className="text-[#B89F82]" />
                <span className="label-overline" style={{ color: "#B89F82" }}>Hours</span>
              </div>
              <p className="text-base font-light">Monday — Saturday · 8:00 AM — 6:00 PM</p>
              <p className="text-base font-light text-[#9A958D]">Sunday · Closed</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const collectionLink = (label) => (
    <li>
      <button
        onClick={() => scrollTo("collection")}
        className="link-elegant text-left hover:text-[#F9F8F6] transition-colors"
      >
        {label}
      </button>
    </li>
  );

  const companyLink = (label, id) => (
    <li>
      <button
        onClick={() => scrollTo(id)}
        className="link-elegant text-left hover:text-[#F9F8F6] transition-colors"
      >
        {label}
      </button>
    </li>
  );

  return (
    <footer
      data-testid={FOOTER.root}
      className="bg-[#0F0F0F] text-[#9A958D] border-t border-white/5"
    >
      <div className="container-wide py-14 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="inline-block bg-white p-3">
            <img
              src="/logo.png"
              alt="In Stone Supplies"
              className="h-14 w-auto object-contain"
            />
          </div>
          <p className="mt-6 max-w-sm text-sm leading-relaxed">
            Premium stone and tile, sourced for the South Florida architectural community.
          </p>
        </div>
        <div className="md:col-span-3">
          <p className="label-overline" style={{ color: "#B89F82" }}>The Collection</p>
          <ul className="mt-4 space-y-2 text-sm">
            {collectionLink("Porcelain Tiles")}
            {collectionLink("Ceramic Tiles")}
            {collectionLink("Marble")}
            {collectionLink("Pavers")}
            {collectionLink("Travertine")}
          </ul>
        </div>
        <div className="md:col-span-3">
          <p className="label-overline" style={{ color: "#B89F82" }}>Company</p>
          <ul className="mt-4 space-y-2 text-sm">
            {companyLink("About", "about")}
            {companyLink("Careers", "careers")}
            {companyLink("Contact", "contact")}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="container-wide py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#6F6B65]">
          <p>© {new Date().getFullYear()} In Stone Supplies. All rights reserved.</p>
          <p className="tracking-[0.2em] uppercase">instonesupplies.com</p>
        </div>
      </div>
    </footer>
  );
};
